import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Heart, PawPrint, Stethoscope, ShoppingBag, Users, ArrowRight, Gift,
  Building2, Sparkles, MapPin, Star, ShieldCheck, Pill, FileText, QrCode, HandHeart, Activity, Globe2, Clock,
  Crown, Check, Syringe, Calendar, Languages, Scissors, Footprints, Truck, Wallet, Menu, X
} from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../App";
import Onboarding from "../components/Onboarding";
import VideoShowcase from "../components/VideoShowcase";
import { useI18n, LanguageSwitcher } from "../i18n";
import { track } from "../lib/analytics";

const HERO_IMG = "https://images.unsplash.com/photo-1546419031-2f09ee2293d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxoYXBweSUyMGRvZyUyMGdvbGRlbiUyMHJldHJpZXZlciUyMG91dGRvb3J8ZW58MHx8fHwxNzc5OTAyMTM2fDA&ixlib=rb-4.1.0&q=85";
const HERO_CAT_IMG = "https://images.unsplash.com/photo-1574158622682-e40e69881006?w=600&q=80&auto=format&fit=crop";
const VET_IMG = "https://images.pexels.com/photos/7470634/pexels-photo-7470634.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const RESCUE_IMG = "https://images.unsplash.com/photo-1517849845537-4d257902454a?w=900&q=80";
const SHOP_IMG = "https://images.unsplash.com/photo-1606214174585-fe31582dc6ee?w=940&q=80&auto=format&fit=crop";
const HOSTEL_IMG = "https://images.pexels.com/photos/4587979/pexels-photo-4587979.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// Map any role → its primary dashboard route.
function dashHomeFor(role) {
  if (role === "vet")          return "/app/vet-dashboard";
  if (role === "store_owner")  return "/app/store-dashboard";
  if (role === "hostel_owner") return "/app/hostel-dashboard";
  if (role === "ngo")          return "/app/ngo/impact";
  if (role === "groomer" || role === "walker") return "/app/services-dashboard";
  if (role === "super_admin")  return "/app/admin";
  return "/app";
}

export default function Landing() {
  const { t } = useI18n();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [ref, setRef] = useState(null);
  const [plans, setPlans] = useState(null); // null = loading, [] = no public plans

  // Fetch admin-managed pricing plans. Falls back to the bundled `PRICING`
  // constant if the API is unreachable or returns empty.
  useEffect(() => {
    axios.get(`${API}/landing-pricing`)
      .then(r => setPlans(Array.isArray(r.data?.plans) && r.data.plans.length > 0 ? r.data.plans : PRICING_FALLBACK))
      .catch(() => setPlans(PRICING_FALLBACK));
  }, []);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Already signed in? Skip the public landing and drop them on their post-signin
  // intent (eg /app/pet-registration) if set, otherwise their role dashboard.
  useEffect(() => {
    if (loading || !user) return;
    let intent = null;
    try { intent = localStorage.getItem("petbond_post_signin"); } catch {}
    if (intent) {
      try { localStorage.removeItem("petbond_post_signin"); } catch {}
      navigate(intent, { replace: true });
      return;
    }
    navigate(dashHomeFor(user.role), { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    track("landing_view");
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("ref");
    if (code) {
      try { localStorage.setItem("petbond_ref", code); } catch {}
      axios.get(`${API}/referrals/preview/${code}`).then(r => setRef(r.data)).catch(() => {});
    } else {
      try { const c = localStorage.getItem("petbond_ref"); if (c) axios.get(`${API}/referrals/preview/${c}`).then(r => setRef(r.data)).catch(() => {}); } catch {}
    }
  }, []);

  const goSignIn = async () => {
    track("signup_started", { entry: "google_oauth" });
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect_uri = `${window.location.origin}/auth/google`;
    try {
      const r = await axios.get(`${API}/auth/google/login`, { params: { redirect_uri } });
      window.location.href = r.data.auth_url;
    } catch (e) {
      const redirect = encodeURIComponent(`${window.location.origin}/app`);
      window.location.href = `https://auth.emergentagent.com/?redirect=${redirect}`;
    }
  };
  const goPartner = () => { try { localStorage.setItem("petbond_partner_intent", "1"); } catch {} goSignIn(); };
  // Deep-link CTAs: stash an intent before sign-in. If user is already signed-in,
  // route straight there (skipping the dashboard redirect from useEffect).
  const goSignInTo = (path) => {
    try { localStorage.setItem("petbond_post_signin", path); } catch {}
    if (user) { navigate(path); return; }
    goSignIn();
  };
  const goPetRegistration = () => goSignInTo("/app/pet-registration");
  const replayTour = () => { try { localStorage.removeItem("petbond_onboarded_v1"); } catch {} window.location.reload(); };

  return (
    <div className="min-h-screen bg-[#FFFDF7] text-ink selection:bg-brand-blue/20 selection:text-ink" data-testid="landing-page">
      <Onboarding onSignIn={goSignIn}/>

      <header className={`sticky top-0 z-40 transition-all ${scrolled ? "bg-bg/95 backdrop-blur-xl border-b border-line" : "bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-3 flex items-center justify-between gap-4">
          <Link to="/" data-testid="brand-logo" className="flex items-center shrink-0">
            <img src="/brand/petbond-logo.png" alt="PetBond — Meet. Care. Adopt. Shop." className="h-14 md:h-16 w-auto" />
          </Link>
          <nav className="hidden lg:flex items-center gap-5 text-sm font-bold text-ink/70 whitespace-nowrap">
            <a href="#features" className="hover:text-ink transition">{t("nav.features")}</a>
            <a href="#shop" className="hover:text-ink transition">Shop</a>
            <a href="#hostels" className="hover:text-ink transition">Hostels</a>
            <a href="#vet-care" className="hover:text-ink transition">{t("nav.vet_care")}</a>
            <a href="#licence" className="hover:text-ink transition inline-flex items-center gap-1">
              Pet Licence <span className="text-[9px] font-bold tracking-widest uppercase bg-brand text-white rounded-full px-1.5 py-0.5">NEW</span>
            </a>
            <a href="#pricing" className="hover:text-ink transition">{t("nav.pricing")}</a>
          </nav>
          <div className="flex items-center gap-2">
            <LanguageSwitcher className="hidden md:inline-flex"/>
            <button data-testid="nav-partner-button" onClick={goPartner} className="hidden sm:inline-flex text-sm font-bold text-ink/80 hover:text-brand px-3 py-2">
              {t("nav.list_business")}
            </button>
            <button data-testid="nav-signin-button" onClick={goSignIn} className="bg-ink text-white rounded-full px-5 py-2.5 text-sm font-bold inline-flex items-center gap-1.5 hover:bg-brand transition-colors">
              {t("nav.signin")} <ArrowRight className="w-4 h-4"/>
            </button>
            {/* Mobile hamburger — visible below lg, since section nav hides at that breakpoint */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              data-testid="mobile-menu-open"
              aria-label="Open menu"
              className="lg:hidden w-11 h-11 rounded-full grid place-items-center hover:bg-surface-muted active:bg-surface-muted -mr-1"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]" data-testid="landing-mobile-menu">
          <div onClick={() => setMobileMenuOpen(false)} className="absolute inset-0 bg-ink/50 backdrop-blur-sm"/>
          <aside className="absolute right-0 top-0 bottom-0 w-[84vw] max-w-[340px] bg-bg shadow-2xl overflow-y-auto flex flex-col">
            <header className="sticky top-0 bg-bg/95 backdrop-blur-xl border-b border-line px-4 py-3 flex items-center justify-between">
              <img src="/brand/petbond-logo.png" alt="PetBond" className="h-10"/>
              <button onClick={() => setMobileMenuOpen(false)} data-testid="landing-mobile-menu-close" aria-label="Close menu" className="w-11 h-11 -mr-2 rounded-full grid place-items-center hover:bg-surface-muted">
                <X className="w-5 h-5"/>
              </button>
            </header>
            <nav className="flex-1 px-2 py-3 space-y-0.5">
              {[
                { href: "#features", label: t("nav.features") },
                { href: "#shop", label: "Shop" },
                { href: "#hostels", label: "Hostels" },
                { href: "#vet-care", label: t("nav.vet_care") },
                { href: "#licence", label: "Pet Licence", badge: "NEW" },
                { href: "#pricing", label: t("nav.pricing") },
              ].map(item => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`landing-mobile-link-${item.href.slice(1)}`}
                  className="flex items-center justify-between px-3 py-3 rounded-xl text-sm font-bold text-ink hover:bg-surface-muted min-h-[44px]"
                >
                  <span>{item.label}</span>
                  {item.badge && <span className="text-[9px] font-bold tracking-widest uppercase bg-brand text-white rounded-full px-1.5 py-0.5">{item.badge}</span>}
                </a>
              ))}
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-ink-soft px-3 pt-4 pb-1.5">Get started</div>
              <button
                onClick={() => { setMobileMenuOpen(false); goPartner(); }}
                data-testid="landing-mobile-partner"
                className="w-full text-left flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-bold text-ink hover:bg-surface-muted min-h-[44px]"
              >
                <Building2 className="w-4 h-4"/> {t("nav.list_business")}
              </button>
              <button
                onClick={() => { setMobileMenuOpen(false); goSignIn(); }}
                data-testid="landing-mobile-signin"
                className="w-full mt-2 bg-brand text-white rounded-full px-4 py-3 text-sm font-bold inline-flex items-center justify-center gap-1.5 hover:bg-brand-hover min-h-[44px]"
              >
                {t("nav.signin")} <ArrowRight className="w-4 h-4"/>
              </button>
            </nav>
            <footer className="px-4 py-4 border-t border-line text-[11px] text-ink-soft">
              <a href="mailto:hello@petbond.in" className="font-bold hover:text-brand">hello@petbond.in</a>
              <div className="mt-1">© {new Date().getFullYear()} PetBond</div>
            </footer>
          </aside>
        </div>
      )}

      {ref && (
        <div className="bg-leaf/40 border-b border-leaf py-2">
          <div className="max-w-7xl mx-auto px-5 md:px-10 text-sm font-bold text-ink flex items-center gap-2">
            <Gift className="w-4 h-4 text-brand"/> {ref.referrer_name} sent you 30 days of PetBond Premium — sign in to claim 🎁
          </div>
        </div>
      )}

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-20 -left-32 w-[28rem] h-[28rem] bg-brand/15 rounded-full blur-3xl"/>
        <div className="absolute -bottom-20 -right-20 w-[28rem] h-[28rem] bg-brand-blue/15 rounded-full blur-3xl"/>
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 pt-12 md:pt-20 pb-20 grid lg:grid-cols-[1.05fr_1fr] gap-10 items-center">
          <div>
            <div data-testid="hero-tagline" className="inline-flex items-center gap-2 bg-leaf/40 border border-leaf rounded-full px-4 py-1.5 text-[11px] font-bold tracking-[0.18em] uppercase text-ink">
              <span className="w-1.5 h-1.5 rounded-full bg-brand"/> {t("hero.tagline")}
            </div>
            <h1 className="font-heading text-5xl md:text-7xl font-extrabold leading-[1.02] tracking-tight mt-5">
              {t("hero.headline_a")}<br/>
              <span className="text-brand italic font-medium">{t("hero.headline_em")}</span> {t("hero.headline_b")}
            </h1>
            <p className="text-ink-soft text-lg mt-6 max-w-xl leading-relaxed">{t("hero.sub")}</p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button data-testid="hero-cta-primary" onClick={goSignIn} className="bg-brand text-white rounded-full px-7 py-4 font-bold text-base inline-flex items-center gap-2 hover:bg-brand-hover shadow-[0_8px_30px_rgb(255,107,53,0.35)] hover:-translate-y-0.5 transition-all">
                {t("hero.cta_primary")} <ArrowRight className="w-4 h-4"/>
              </button>
              <a href="#features" className="text-ink/85 px-5 py-3 rounded-full text-sm font-bold border border-ink/10 hover:bg-ink/5 transition">{t("hero.cta_secondary")}</a>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-7 gap-y-3 text-xs font-bold text-ink-soft">
              <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-brand-green"/> No card · 7-day free trial</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-brand-green"/> KYC-verified vets</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-brand-green"/> 100% to NGOs</span>
            </div>
          </div>
          <div className="relative">
            <div className="relative rounded-[2rem] overflow-hidden shadow-[0_30px_60px_-15px_rgba(0,0,0,0.18)]">
              <img src={HERO_IMG} alt="Happy dog" fetchPriority="high" loading="eager" className="w-full h-[520px] object-cover"/>
            </div>
            {/* Cat companion — overlapping tile so the hero shows BOTH dogs & cats */}
            <div className="absolute -top-3 right-3 md:-top-4 md:-right-6 w-24 h-24 md:w-44 md:h-44 lg:w-52 lg:h-52 rounded-2xl md:rounded-[1.5rem] overflow-hidden border-[3px] md:border-4 border-bg shadow-soft rotate-[3deg] animate-[float_6.5s_ease-in-out_infinite] [animation-delay:.8s]" data-testid="hero-cat-tile">
              <img src={HERO_CAT_IMG} alt="Curious cat" loading="eager" className="w-full h-full object-cover"/>
              <span className="absolute bottom-1 left-1 md:bottom-1.5 md:left-1.5 bg-ink text-white text-[8px] md:text-[10px] font-bold tracking-wider uppercase rounded-full px-1.5 md:px-2 py-0.5">+ cats too</span>
            </div>
            {/* Floating UI cards */}
            <div className="absolute top-5 -left-3 md:-left-7 bg-surface rounded-2xl shadow-soft p-3.5 w-60 border border-line animate-[float_5s_ease-in-out_infinite]">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-full bg-brand grid place-items-center"><Sparkles className="w-3.5 h-3.5 text-white"/></div>
                <div className="text-xs font-extrabold">Dr. PetBond AI</div>
                <div className="ml-auto text-[10px] font-bold bg-leaf/60 rounded-full px-2 py-0.5">24/7</div>
              </div>
              <div className="text-xs leading-relaxed"><b>Try bland boiled rice + chicken</b>. If vomiting persists → visit a vet.</div>
            </div>
            <div className="absolute top-32 -right-3 md:-right-7 bg-surface rounded-2xl shadow-soft p-3.5 w-56 border border-line animate-[float_6s_ease-in-out_infinite] [animation-delay:1s]">
              <div className="flex items-center gap-2"><Syringe className="w-4 h-4 text-brand-blue"/><div className="text-xs font-extrabold">Vaccination due</div></div>
              <div className="text-[11px] text-ink-soft mt-1">Buddy · Rabies booster</div>
              <div className="mt-2 inline-flex items-center justify-between w-full bg-sun rounded-full px-2.5 py-1 text-[11px] font-bold text-ink">📅 Tomorrow, 11am <span className="text-brand">Book →</span></div>
            </div>
            <div className="absolute bottom-5 -left-3 md:-left-6 bg-ink text-white rounded-2xl p-3.5 w-60 shadow-soft animate-[float_5.5s_ease-in-out_infinite] [animation-delay:.5s]">
              <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-leaf"/><div className="text-xs font-extrabold">Near me · 25 km radar</div></div>
              <div className="text-[11px] opacity-80 mt-1">Vets, stores & hostels — nearest first</div>
            </div>
            <div className="absolute bottom-5 -right-3 md:-right-6 bg-brand text-white rounded-2xl p-3.5 w-44 shadow-soft animate-[float_4.5s_ease-in-out_infinite] [animation-delay:1.2s]">
              <div className="text-[9px] font-bold tracking-widest uppercase opacity-80">Premium</div>
              <div className="text-xl font-extrabold">7 days <span className="font-medium italic">free</span></div>
              <div className="text-[11px] opacity-90">No card. Cancel anytime.</div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR — honest capability statements (no fabricated traction) */}
      <section className="border-y border-line bg-bg/60">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <Stat n="Built for" l="100M+ pet households"/>
          <Stat n="Near me" l="25 km nearby-first radar"/>
          <Stat n="KYC" l="vets · stores · NGOs"/>
          <Stat n="ABC 2023" l="court-ready register"/>
        </div>
      </section>

      {/* COMPLIANCE & TRUST — honest, factual claims only (no unofficial trademark use) */}
      <section className="bg-surface-muted/50 border-b border-line" data-testid="landing-recognitions">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-8">
          <div className="text-center mb-6">
            <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-ink-soft">Compliance & Trust</div>
            <div className="text-xs text-ink-soft mt-1">Built for Indian regulations · Made-in-India tech stack</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 items-stretch">
            {/* DPDP Act 2023 — live */}
            <div data-testid="badge-dpdp" className="bg-surface border border-line rounded-2xl p-4 flex items-center gap-3 hover:border-brand/40 transition-colors">
              <div className="shrink-0 w-11 h-11 rounded-xl grid place-items-center bg-gradient-to-br from-[#1F4D3E] to-[#0F2E24] text-white border border-[#0F2E24]/40">
                <ShieldCheck className="w-5 h-5"/>
              </div>
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-wider text-ink-soft">DPDP Act 2023</div>
                <div className="font-heading text-sm font-extrabold leading-tight truncate">Privacy compliant</div>
                <div className="text-[10px] text-ink-soft mt-0.5">Right to erasure live</div>
              </div>
            </div>

            {/* GDPR-ready */}
            <div data-testid="badge-gdpr" className="bg-surface border border-line rounded-2xl p-4 flex items-center gap-3 hover:border-brand/40 transition-colors">
              <div className="shrink-0 w-11 h-11 rounded-xl grid place-items-center bg-gradient-to-br from-[#3B4A9E] to-[#1E2A5E] text-white border border-[#1E2A5E]/40">
                <span className="text-[10px] font-black tracking-widest">GDPR</span>
              </div>
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-wider text-ink-soft">EU Regulation</div>
                <div className="font-heading text-sm font-extrabold leading-tight truncate">GDPR-ready</div>
                <div className="text-[10px] text-ink-soft mt-0.5">Data portability live</div>
              </div>
            </div>

            {/* ABC Rules 2023 */}
            <div data-testid="badge-abc" className="bg-surface border border-line rounded-2xl p-4 flex items-center gap-3 hover:border-brand/40 transition-colors">
              <div className="shrink-0 w-11 h-11 rounded-xl grid place-items-center bg-gradient-to-br from-[#8A5A2B] to-[#4E3216] text-white border border-[#4E3216]/40">
                <span className="text-[10px] font-black tracking-widest">ABC</span>
              </div>
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-wider text-ink-soft">AWBI · 2023</div>
                <div className="font-heading text-sm font-extrabold leading-tight truncate">ABC-Rules aligned</div>
                <div className="text-[10px] text-ink-soft mt-0.5">Court-ready register</div>
              </div>
            </div>

            {/* Razorpay verified payments */}
            <div data-testid="badge-razorpay" className="bg-surface border border-line rounded-2xl p-4 flex items-center gap-3 hover:border-brand/40 transition-colors">
              <div className="shrink-0 w-11 h-11 rounded-xl grid place-items-center bg-gradient-to-br from-[#02042B] to-[#0B1550] text-white border border-[#0B1550]/40">
                <span className="text-[10px] font-black tracking-widest">RBI</span>
              </div>
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-wider text-ink-soft">Payments · India</div>
                <div className="font-heading text-sm font-extrabold leading-tight truncate">Razorpay secured</div>
                <div className="text-[10px] text-ink-soft mt-0.5">PCI-DSS gateway</div>
              </div>
            </div>
          </div>
          <div className="text-center text-[10px] text-ink-soft mt-4 italic">
            PetBond Technologies · Noida, Uttar Pradesh · Made-in-India · Government registrations in progress
          </div>
        </div>
      </section>

      {/* PROMO VIDEOS — admin uploaded */}
      <VideoShowcase section="" eyebrow="Watch · 60 seconds" title="See PetBond in action."/>

      {/* FEATURE PILLARS — 6 meaningful groups, not 16 cards */}
      <section id="features" className="max-w-7xl mx-auto px-5 md:px-10 py-24">
        <div className="max-w-2xl mb-14">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-brand">{t("feat.section_eyebrow")}</div>
          <h2 className="font-heading text-4xl md:text-6xl font-extrabold mt-3 tracking-tight leading-[1.02]">{t("feat.section_heading")}</h2>
          <p className="text-ink-soft mt-5 text-lg max-w-xl">Six pillars. One ecosystem. Hover any card to see what's inside.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PILLARS.map((p, i) => (
            <article key={i} data-testid={`pillar-card-${i}`} className="group relative bg-surface border border-line rounded-[1.75rem] p-7 hover:-translate-y-1.5 hover:shadow-hover transition-all overflow-hidden">
              {/* decorative accent */}
              <div className={`absolute -top-12 -right-12 w-40 h-40 rounded-full ${p.glow} opacity-50 group-hover:opacity-80 blur-2xl transition-opacity`}/>
              <div className={`relative w-14 h-14 rounded-2xl grid place-items-center ${p.color} shadow-[0_8px_24px_-8px_rgba(0,0,0,0.25)]`}>
                <p.icon className="w-7 h-7" strokeWidth={2}/>
              </div>
              <h3 className="relative font-heading font-extrabold text-2xl mt-5 leading-tight">{p.title}</h3>
              <p className="relative text-sm text-ink-soft mt-2 leading-relaxed">{p.summary}</p>

              {/* On-hover expand — feature pills with tooltips */}
              <div className="relative mt-5 grid grid-cols-2 gap-1.5">
                {p.features.slice(0, 4).map((f, fi) => (
                  <span key={fi} title={f.tip} className="text-[10.5px] font-bold uppercase tracking-wider bg-surface-muted/70 hover:bg-brand hover:text-white rounded-full px-2.5 py-1 inline-flex items-center gap-1 truncate cursor-default transition-colors">
                    <f.icon className="w-3 h-3 shrink-0"/> <span className="truncate">{f.label}</span>
                  </span>
                ))}
              </div>
              {p.badge && <span className="absolute top-5 right-5 text-[10px] font-bold uppercase tracking-wider bg-leaf/70 rounded-full px-2.5 py-1">{p.badge}</span>}
            </article>
          ))}
        </div>
      </section>

      {/* RESCUE-TO-HOME — investor story */}
      <section id="rescue" className="bg-ink text-bg py-24 relative overflow-hidden">
        <div className="absolute -top-20 right-0 w-96 h-96 bg-brand/20 rounded-full blur-3xl"/>
        <div className="absolute -bottom-20 left-0 w-96 h-96 bg-brand-green/15 rounded-full blur-3xl"/>
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs font-bold tracking-[0.25em] uppercase text-brand">{t("rescue.eyebrow")}</div>
            <h2 className="font-heading text-4xl md:text-6xl font-extrabold mt-3 leading-[1.02]">{t("rescue.heading")}</h2>
            <p className="opacity-80 mt-5 text-lg max-w-lg">{t("rescue.sub")}</p>
            <div className="mt-8 space-y-3">
              {[
                { ic: HandHeart, t: "1. Stray reported / found", s: "NGO logs intake + photo" },
                { ic: Stethoscope, t: "2. Pro-bono vet treats", s: "Goodwill counter for the clinic" },
                { ic: Heart, t: "3. Foster family takes over", s: "ID + home photo verified" },
                { ic: Sparkles, t: "4. Sponsored or adopted forever", s: "₹500/mo sponsorship keeps the lights on" },
              ].map((s, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-bg/10 grid place-items-center shrink-0"><s.ic className="w-4 h-4 text-leaf"/></div>
                  <div><div className="font-bold text-sm">{s.t}</div><div className="text-xs opacity-70">{s.s}</div></div>
                </div>
              ))}
            </div>
            <button onClick={goSignIn} data-testid="rescue-cta" className="mt-8 bg-brand text-white rounded-full px-6 py-3.5 font-bold inline-flex items-center gap-2 hover:bg-brand-hover">Browse rescues <ArrowRight className="w-4 h-4"/></button>
          </div>
          <div className="relative">
            <img src={RESCUE_IMG} alt="Rescued dog" className="rounded-[2rem] w-full h-[460px] object-cover"/>
            <div className="absolute -bottom-6 -left-6 bg-surface text-ink rounded-2xl shadow-soft p-4 w-64 border border-line">
              <div className="text-[10px] font-bold tracking-wider uppercase text-brand">Open for partners</div>
              <div className="font-heading text-2xl font-extrabold mt-1">NGO onboarding live</div>
              <div className="text-[11px] text-ink-soft">KYC + 80G + ABC 2023 register — invite your shelter today</div>
            </div>
            <div className="absolute -top-4 -right-4 bg-brand-green text-white rounded-2xl shadow-soft p-4 w-44">
              <div className="text-[10px] font-bold tracking-wider uppercase opacity-80">Sponsorship</div>
              <div className="font-heading text-3xl font-extrabold">100%</div>
              <div className="text-[11px] opacity-90">to verified NGOs</div>
            </div>
          </div>
        </div>
      </section>

      {/* NGO COMPLIANCE — the breakthrough story */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-24 grid lg:grid-cols-2 gap-12 items-center">
        <div className="relative order-2 lg:order-1">
          <div className="bg-surface border border-line rounded-3xl p-6 shadow-soft">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs font-bold tracking-wider uppercase text-brand">Animal Register · ABC Rules 2023</div>
                <div className="font-heading text-xl font-extrabold mt-1">Your NGO · Your city <span className="text-[10px] font-bold text-ink-soft uppercase tracking-wider ml-1">Preview</span></div>
              </div>
              <div className="bg-brand-green text-white rounded-full px-3 py-1 text-[10px] font-bold inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3"/> Court-ready</div>
            </div>
            <div className="grid grid-cols-4 gap-2 mb-4">
              {[["Animals", "127"], ["ABC done", "118"], ["ARV done", "124"], ["Released", "92"]].map(([l, v], i) => (
                <div key={i} className="bg-surface-muted/60 rounded-xl p-2.5 text-center">
                  <div className="font-heading text-lg font-extrabold">{v}</div>
                  <div className="text-[9px] uppercase tracking-wider font-bold text-ink-soft">{l}</div>
                </div>
              ))}
            </div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-ink-soft mb-2">Sample entries (yours will go here)</div>
            <div className="space-y-1.5">
              {[["FRD-0127", "Hope", "92%", "bg-brand-green"], ["FRD-0126", "Pebbles", "100%", "bg-brand-green"], ["FRD-0125", "Bruno", "67%", "bg-sun"]].map(([id, n, p, c], i) => (
                <div key={i} className="flex items-center gap-3 bg-bg/40 rounded-xl p-2.5">
                  <div className="font-bold text-xs w-20">{id}</div>
                  <div className="flex-1 text-xs">{n}</div>
                  <div className={`w-12 h-1.5 rounded-full bg-line overflow-hidden`}><div className={`h-full ${c}`} style={{width: p}}/></div>
                  <div className="text-[11px] font-bold w-9 text-right">{p}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex gap-2">
              <div className="flex-1 bg-brand text-white rounded-full px-3 py-2 text-xs font-bold text-center">Export PDF →</div>
              <div className="flex-1 bg-ink text-white rounded-full px-3 py-2 text-xs font-bold text-center">CSV →</div>
            </div>
          </div>
        </div>
        <div className="order-1 lg:order-2">
          <div className="text-xs font-bold tracking-[0.25em] uppercase text-brand">{t("ngo.eyebrow")}</div>
          <h2 className="font-heading text-4xl md:text-6xl font-extrabold mt-3 leading-[1.02] tracking-tight">{t("ngo.heading")}</h2>
          <p className="text-ink-soft mt-5 text-lg max-w-lg">{t("ngo.sub")}</p>
          <ul className="mt-7 space-y-3">
            {[
              "Tag #, capture/release GPS, sterilization, anti-rabies — every field a court can ask for.",
              "Auto-syncs sterilization + ARV from PetBond vets. Zero double-entry.",
              "One-click PDF in your NGO's letterhead. Print at the inspection desk.",
              "Compliance score per animal — fix what's missing before they ask.",
            ].map((line, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm">
                <Check className="w-4 h-4 text-brand-green mt-0.5 shrink-0"/>
                <span className="text-ink-soft leading-relaxed">{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* VET CARE BAND */}
      <section id="vet-care" className="relative bg-[#1A1A2E] text-bg py-24 overflow-hidden">
        <div className="absolute top-10 right-10 w-72 h-72 bg-leaf/20 rounded-full blur-3xl"/>
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-[1fr_1.05fr] gap-12 items-center">
          <div>
            <div className="text-xs font-bold tracking-[0.25em] uppercase text-leaf">For pet parents</div>
            <h2 className="font-heading text-4xl md:text-6xl font-extrabold mt-3 leading-[1.02]">Vet care, finally — without the wait.</h2>
            <p className="opacity-80 mt-5 text-lg max-w-lg">Pick a slot in 60 seconds. Walk in. Walk out with a digital prescription + vaccination schedule that auto-reminds you.</p>
            <div className="mt-8 grid grid-cols-2 gap-3">
              {[[Calendar, "Real-time slots"], [Pill, "Digital Rx"], [Syringe, "Auto reminders"], [FileText, "Pet Passport"]].map(([Ic, l], i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-3.5">
                  <Ic className="w-4 h-4 text-leaf"/>
                  <div className="font-bold text-sm mt-1">{l}</div>
                </div>
              ))}
            </div>
            <button onClick={goSignIn} className="mt-8 bg-brand text-white rounded-full px-6 py-3.5 font-bold inline-flex items-center gap-2 hover:bg-brand-hover">Find a vet near you <ArrowRight className="w-4 h-4"/></button>
          </div>
          <div className="relative">
            <img src={VET_IMG} alt="Vet" className="w-full h-[460px] object-cover rounded-[2rem]"/>
            <div className="absolute -bottom-6 -left-6 bg-surface text-ink rounded-2xl shadow-soft p-4 w-72 border border-line">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-brand/15 grid place-items-center text-brand"><Stethoscope className="w-5 h-5"/></div>
                <div>
                  <div className="font-bold text-sm">Your verified vet</div>
                  <div className="text-[11px] text-ink-soft">Live calendar · digital Rx</div>
                </div>
                <div className="ml-auto bg-leaf/60 text-ink rounded-full px-2 py-0.5 text-[9px] font-bold">KYC ✓</div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {["10:00", "11:30", "2:00", "4:30"].map(t2 => (
                  <span key={t2} className="text-[10px] font-bold bg-surface-muted rounded-full px-2 py-0.5">{t2}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PET SHOP / MARKETPLACE — highlight */}
      <section id="shop" className="relative py-24 overflow-hidden bg-gradient-to-br from-bg via-surface to-bg">
        <div className="absolute top-10 -left-20 w-80 h-80 bg-brand/15 rounded-full blur-3xl"/>
        <div className="absolute bottom-10 -right-20 w-80 h-80 bg-brand-blue/15 rounded-full blur-3xl"/>
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-[1.05fr_1fr] gap-12 items-center">
          <div className="relative order-2 lg:order-1">
            <img
              src={SHOP_IMG}
              alt="PetBond marketplace — food, toys and supplies"
              className="w-full h-[460px] object-cover rounded-[2rem] shadow-soft"
              loading="lazy"
            />
            {/* Floating cart card */}
            <div className="absolute -bottom-6 -right-6 bg-surface text-ink rounded-2xl shadow-soft p-4 w-72 border border-line" data-testid="shop-cart-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-brand/15 grid place-items-center text-brand"><ShoppingBag className="w-5 h-5"/></div>
                <div className="min-w-0">
                  <div className="font-bold text-sm truncate">Happy Tails Pet Mart</div>
                  <div className="text-[11px] text-ink-soft">1.2 km · Free pickup option</div>
                </div>
                <div className="ml-auto bg-brand-green/80 text-white rounded-full px-2 py-0.5 text-[9px] font-bold">OPEN</div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-1.5 text-[10px] font-bold">
                <div className="bg-surface-muted rounded-lg p-1.5 text-center">Food</div>
                <div className="bg-surface-muted rounded-lg p-1.5 text-center">Toys</div>
                <div className="bg-surface-muted rounded-lg p-1.5 text-center">Meds</div>
              </div>
              <div className="mt-3 flex items-center justify-between text-[11px]">
                <span className="text-ink-soft">3 items · 1 cart</span>
                <span className="font-extrabold text-brand">₹1,240</span>
              </div>
            </div>
            {/* Floating "nearby" sticker */}
            <div className="absolute -top-4 -left-4 bg-ink text-white rounded-2xl shadow-soft p-3 w-36 animate-[float_5s_ease-in-out_infinite]">
              <div className="text-[9px] font-bold tracking-widest uppercase opacity-80">Nearby-first</div>
              <div className="font-heading text-lg font-extrabold leading-tight">25 km<br/>default radius</div>
            </div>
          </div>

          <div className="order-1 lg:order-2">
            <div className="text-xs font-bold tracking-[0.25em] uppercase text-brand">Pet Shop · Marketplace</div>
            <h2 className="font-heading text-4xl md:text-6xl font-extrabold mt-3 leading-[1.02] tracking-tight">
              Everything your pet needs — <span className="text-brand italic font-medium">from the shop next door.</span>
            </h2>
            <p className="text-ink-soft mt-5 text-lg max-w-lg">
              Hyper-local catalogues, one cart per store (like Swiggy), and pickup-or-delivery in your neighbourhood.
              Stores keep more of each sale; you skip the warehouse markup.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-3">
              {[
                { ic: MapPin,      t: "Nearby-first",      s: "Stores sorted by distance · 25 km default" },
                { ic: ShoppingBag, t: "One-store cart",    s: "One delivery, one bill — Swiggy-style" },
                { ic: Pill,        t: "Meds + food + toys",s: "Curated SKUs, Rx-only items gated" },
                { ic: Wallet,      t: "Weekly payouts",    s: "Auto Razorpay Route to store UPI" },
              ].map((it, i) => (
                <div key={i} className="bg-surface border border-line rounded-2xl p-4" data-testid={`shop-feature-${i}`}>
                  <it.ic className="w-4 h-4 text-brand"/>
                  <div className="font-bold text-sm mt-1.5">{it.t}</div>
                  <div className="text-[11px] text-ink-soft mt-0.5">{it.s}</div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                data-testid="shop-cta-shop"
                onClick={() => goSignInTo("/app/store")}
                className="bg-brand text-white rounded-full px-6 py-3.5 font-bold inline-flex items-center gap-2 hover:bg-brand-hover shadow-[0_8px_30px_rgb(255,107,53,0.3)] hover:-translate-y-0.5 transition-all"
              >
                Browse nearby stores <ArrowRight className="w-4 h-4"/>
              </button>
              <button
                data-testid="shop-cta-list"
                onClick={goPartner}
                className="bg-ink text-white rounded-full px-5 py-3.5 text-sm font-bold inline-flex items-center gap-2 hover:bg-brand transition-colors"
              >
                List your store
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* PET HOSTEL / BOARDING — highlight */}
      <section id="hostels" className="relative bg-[#0F1F1B] text-bg py-24 overflow-hidden">
        <div className="absolute top-10 left-10 w-80 h-80 bg-leaf/20 rounded-full blur-3xl"/>
        <div className="absolute bottom-10 right-10 w-80 h-80 bg-sun/15 rounded-full blur-3xl"/>
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-[1fr_1.05fr] gap-12 items-center">
          <div>
            <div className="text-xs font-bold tracking-[0.25em] uppercase text-sun">Pet Hostels · Boarding</div>
            <h2 className="font-heading text-4xl md:text-6xl font-extrabold mt-3 leading-[1.02] tracking-tight">
              Travelling? Your pet <span className="text-sun italic font-medium">vacays too.</span>
            </h2>
            <p className="opacity-80 mt-5 text-lg max-w-lg">
              Verified, cage-free boarding with live capacity, daily rates, and CCTV-friendly options.
              Vet-on-call partnerships built in — because peace of mind shouldn't be an extra.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-3">
              {[
                { ic: Building2,   t: "Live capacity",    s: "Real-time rooms · no \"sold out\" surprises" },
                { ic: Calendar,    t: "Day-by-day rates", s: "Check-in / check-out, pro-rated billing" },
                { ic: ShieldCheck, t: "KYC + CCTV",       s: "Verified hostels; CCTV filter available" },
                { ic: Stethoscope, t: "Vet-on-call",      s: "Partnered vet within 5 km of every hostel" },
              ].map((it, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-3.5" data-testid={`hostel-feature-${i}`}>
                  <it.ic className="w-4 h-4 text-sun"/>
                  <div className="font-bold text-sm mt-1">{it.t}</div>
                  <div className="text-[11px] opacity-70 mt-0.5">{it.s}</div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                data-testid="hostel-cta-find"
                onClick={() => goSignInTo("/app/hostels")}
                className="bg-sun text-ink rounded-full px-6 py-3.5 font-bold inline-flex items-center gap-2 hover:bg-sun/90 hover:-translate-y-0.5 transition-all"
              >
                Find a hostel <ArrowRight className="w-4 h-4"/>
              </button>
              <button
                data-testid="hostel-cta-list"
                onClick={goPartner}
                className="bg-white/10 border border-white/20 text-white rounded-full px-5 py-3.5 text-sm font-bold inline-flex items-center gap-2 hover:bg-white/20 transition-colors"
              >
                List your hostel
              </button>
            </div>
          </div>
          <div className="relative">
            <img
              src={HOSTEL_IMG}
              alt="PetBond hostels — cage-free boarding with live capacity"
              className="w-full h-[460px] object-cover rounded-[2rem]"
              loading="lazy"
            />
            {/* Floating booking card */}
            <div className="absolute -bottom-6 -left-6 bg-surface text-ink rounded-2xl shadow-soft p-4 w-72 border border-line" data-testid="hostel-booking-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-leaf/30 grid place-items-center text-leaf-deep"><Building2 className="w-5 h-5"/></div>
                <div className="min-w-0">
                  <div className="font-bold text-sm truncate">Pawsome Stay · Indiranagar</div>
                  <div className="text-[11px] text-ink-soft">CCTV · cage-free · vet 4 km</div>
                </div>
                <div className="ml-auto bg-leaf/60 text-ink rounded-full px-2 py-0.5 text-[9px] font-bold">3 LEFT</div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {["Sat", "Sun", "Mon", "Tue"].map(d => (
                  <span key={d} className="text-[10px] font-bold bg-surface-muted rounded-full px-2 py-0.5">{d}</span>
                ))}
                <span className="text-[10px] font-bold bg-brand text-white rounded-full px-2 py-0.5">₹650/day</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="licence" className="relative max-w-7xl mx-auto px-5 md:px-10 py-24">
        <div className="absolute -top-10 -left-10 w-72 h-72 bg-brand/10 rounded-full blur-3xl"/>
        <div className="absolute -bottom-10 -right-10 w-72 h-72 bg-brand-green/10 rounded-full blur-3xl"/>
        <div className="relative grid lg:grid-cols-[1.05fr_1fr] gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-brand text-white rounded-full px-3 py-1 text-[10px] font-bold tracking-[0.18em] uppercase">
              <Sparkles className="w-3 h-3"/> New · Concierge Service
            </div>
            <h2 className="font-heading text-4xl md:text-6xl font-extrabold mt-4 leading-[1.02] tracking-tight">
              Municipal pet licence?<br/>
              <span className="text-brand italic font-medium">We'll do it.</span> You relax.
            </h2>
            <p className="text-ink-soft mt-5 text-lg max-w-xl">
              Pet registration is mandatory in most Indian cities — and a nightmare to do alone. PetBond's ops team
              files with the Noida Authority, MCD (Delhi), MCG (Gurgaon), GMC (Ghaziabad), BMC (Mumbai), BBMP (Bengaluru), PMC, GCC, GHMC and more pan-India, follows up, and uploads your certificate.
              <strong className="text-ink"> One flat fee. No queues. No bribes.</strong>
            </p>

            <div className="mt-7 grid sm:grid-cols-2 gap-3 max-w-lg">
              {[
                { ic: Check, t: "Flat ₹999 service fee",  s: "Govt fee billed separately, at cost" },
                { ic: Clock,    t: "14–21 day SLA",          s: "We chase the authority for you" },
                { ic: FileText, t: "Digital certificate",    s: "PDF + courier copy delivered" },
                { ic: ShieldCheck, t: "Refund if rejected",  s: "Govt fee returned in full" },
              ].map((it, i) => (
                <div key={i} className="bg-surface border border-line rounded-2xl p-4">
                  <it.ic className="w-4 h-4 text-brand"/>
                  <div className="font-bold text-sm mt-1.5">{it.t}</div>
                  <div className="text-[11px] text-ink-soft mt-0.5">{it.s}</div>
                </div>
              ))}
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button
                data-testid="licence-cta-primary"
                onClick={goPetRegistration}
                className="bg-brand text-white rounded-full px-6 py-3.5 font-bold inline-flex items-center gap-2 hover:bg-brand-hover shadow-[0_8px_30px_rgb(255,107,53,0.3)] hover:-translate-y-0.5 transition-all"
              >
                Start my registration <ArrowRight className="w-4 h-4"/>
              </button>
              <span className="text-xs font-bold text-ink-soft">No paperwork by you · 100% refund if rejected</span>
            </div>
            <div className="mt-4 text-[11px] font-bold uppercase tracking-wider text-ink-soft">Available now in</div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {["Noida", "Delhi", "Gurgaon", "Ghaziabad", "Mumbai", "Bengaluru", "Pune", "Chennai", "Hyderabad"].map(c => (
                <span key={c} className="text-[11px] font-bold bg-leaf/40 border border-leaf rounded-full px-2.5 py-1">{c}</span>
              ))}
              <span className="text-[11px] font-bold bg-surface-muted rounded-full px-2.5 py-1 text-ink-soft">+ pan-India on request</span>
            </div>
          </div>

          {/* Pricing matrix card */}
          <div className="relative">
            <div className="bg-surface border border-line rounded-[2rem] p-6 shadow-soft">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] font-bold tracking-wider uppercase text-brand">All-inclusive pricing</div>
                  <div className="font-heading text-xl font-extrabold mt-0.5">Pick your city</div>
                </div>
                <div className="bg-brand-green text-white rounded-full px-2.5 py-1 text-[10px] font-bold inline-flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3"/> KYC-clean
                </div>
              </div>
              <div className="space-y-2">
                {[
                  ["Noida", "GNN/Noida Authority", 500, 14],
                  ["Delhi", "MCD", 500, 21],
                  ["Gurgaon", "MCG", 500, 14],
                  ["Ghaziabad", "GMC", 400, 14],
                  ["Mumbai", "BMC", 500, 21],
                  ["Bengaluru", "BBMP", 530, 14],
                  ["Pune", "PMC", 300, 14],
                  ["Chennai", "GCC", 50, 14],
                  ["Hyderabad", "GHMC", 250, 21],
                ].map(([city, auth, gov, sla], i) => (
                  <div key={i} className="flex items-center gap-3 bg-surface-muted/60 rounded-2xl p-3" data-testid={`licence-city-row-${i}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{city}</div>
                      <div className="text-[10px] text-ink-soft uppercase tracking-wider font-bold">{auth} · ~{sla} days</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-ink-soft">You pay</div>
                      <div className="font-heading font-extrabold text-base">₹{gov + 999}</div>
                      <div className="text-[9px] text-ink-soft">₹{gov} govt + ₹999 service</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 bg-ink text-white rounded-2xl px-4 py-3 text-xs flex items-center gap-2">
                <FileText className="w-3.5 h-3.5 text-leaf"/>
                <span className="opacity-90">Certificate delivered to your PetBond inbox + courier copy if requested.</span>
              </div>
            </div>
            {/* Floating sticker */}
            <div className="absolute -top-4 -right-4 bg-brand text-white rounded-2xl shadow-soft p-3 w-36 animate-[float_5s_ease-in-out_infinite]">
              <div className="text-[9px] font-bold tracking-widest uppercase opacity-80">Done by us</div>
              <div className="font-heading text-2xl font-extrabold leading-tight">Zero<br/>paperwork</div>
            </div>
          </div>
        </div>
      </section>

      {/* FOR BUSINESSES — vendor onboarding */}
      <section id="partners" className="max-w-7xl mx-auto px-5 md:px-10 py-24">
        <div className="max-w-2xl mb-12">
          <div className="text-xs font-bold tracking-[0.25em] uppercase text-brand">For Vets · Groomers · Walkers · Stores · Hostels · Vans · NGOs</div>
          <h2 className="font-heading text-4xl md:text-6xl font-extrabold mt-3 leading-[1.02] tracking-tight">List your business. Reach every pet parent.</h2>
          <p className="text-ink-soft mt-5 text-lg max-w-xl">Verified-only marketplace. KYC onboarding. Live bookings. Auto-payouts every Monday. Built for India scale — global day one.</p>
        </div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[
            { ic: Stethoscope, t: "Vet clinics",       l: "Online bookings · digital Rx · inventory", color: "bg-brand text-white", tip: "Run a fully digital clinic — manage real-time slot calendar, in-house pharmacy stock, and digital prescriptions that auto-sync to the pet's passport." },
            { ic: Scissors,    t: "Groomers",          l: "Bath · haircut · nail-care bookings",      color: "bg-brand-blue text-white", tip: "Get bookings from verified pet parents in your area. At-home or at-studio — you choose. Auto weekly payout via Razorpay." },
            { ic: Footprints,  t: "Dog walkers",       l: "Set area · rate · slot availability",      color: "bg-leaf text-ink", tip: "Define your service area, walk type (solo/group/short/long) and rate per walk. Parents book; you confirm and walk." },
            { ic: ShoppingBag, t: "Pet stores",        l: "Catalogue · orders · dashboard",           color: "bg-brand-blue text-white", tip: "List your products on PetBond's storefront. Orders, inventory and earnings ledger in one dashboard." },
            { ic: Building2,   t: "Hostels",           l: "Boarding capacity · check-ins · revenue",  color: "bg-sun text-ink", tip: "Run a boarding hostel with live capacity. CCTV-friendly. Vet-on-call partnerships supported." },
            { ic: Truck,       t: "Mobile Vet Vans",   l: "Schedule drives · auto-notify nearby parents", color: "bg-sky text-white", tip: "Plot a vaccination drive on the map. Pet parents within 15km radius get auto-notified. Anyone can register a van." },
            { ic: HandHeart,   t: "NGOs / Shelters",   l: "Donations · foster · ABC register",        color: "bg-brand-green text-white", tip: "Run a court-ready ABC 2023 register, accept 0%-fee donations, manage foster applications. Investor-grade impact KPIs." },
            { ic: Sparkles,    t: "All earn together", l: "Auto weekly payout · transparent fees",    color: "bg-ink text-white", tip: "Every partner gets an earnings dashboard, weekly auto-payout (Mon), and a clear commission rate set by category." },
          ].map((p, i) => (
            <button key={i} onClick={goPartner} data-testid={`partner-card-${i}`} title={p.tip} className="group relative text-left bg-surface border border-line rounded-3xl p-6 hover:-translate-y-1.5 hover:shadow-hover transition-all">
              <div className={`w-12 h-12 rounded-2xl grid place-items-center ${p.color}`}><p.ic className="w-5 h-5"/></div>
              <h3 className="font-heading font-extrabold mt-4 text-lg leading-tight">{p.t}</h3>
              <p className="text-xs text-ink-soft mt-1.5 leading-relaxed">{p.l}</p>
              <div className="text-xs font-bold text-brand mt-4 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">Onboard <ArrowRight className="w-3.5 h-3.5"/></div>
            </button>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="bg-bg py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-xs font-bold tracking-[0.25em] uppercase text-brand">Simple pricing</div>
            <h2 className="font-heading text-4xl md:text-6xl font-extrabold mt-3 tracking-tight">Free forever. Premium when you need it.</h2>
            <p className="text-ink-soft mt-5">7 days free Premium on every account. No card. Cancel anytime.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5 mt-12">
            {(plans || PRICING_FALLBACK).map((p, i) => {
              const featured = !!p.featured;
              const price = p.price_display || `₹${p.amount || 0}`;
              const unit = p.unit_display || (p.interval_days === 365 ? "/year" : p.interval_days > 0 ? "/month" : "/forever");
              const cta = p.cta_label || p.cta || (p.trial_days > 0 ? `Start ${p.trial_days}-day free trial` : "Subscribe");
              return (
                <div key={p.plan_id || i} className={`rounded-3xl p-7 border ${featured ? "bg-ink text-bg border-ink shadow-[0_30px_60px_-15px_rgba(0,0,0,0.25)] ring-4 ring-brand/20" : "bg-surface border-line"}`}>
                  <div className={`text-xs font-bold tracking-wider uppercase ${featured ? "text-brand" : "text-brand"}`}>{p.tag || (p.side === "vendor" ? "Vendor" : "Parent")}</div>
                  <h3 className="font-heading font-extrabold text-2xl mt-2">{p.name}</h3>
                  <div className="mt-3 flex items-baseline gap-2">
                    <span className="font-heading text-5xl font-extrabold">{price}</span>
                    <span className={`${featured ? "opacity-70" : "text-ink-soft"} text-sm font-bold`}>{unit}</span>
                  </div>
                  {p.tagline && <p className={`${featured ? "opacity-70" : "text-ink-soft"} text-sm mt-2`}>{p.tagline}</p>}
                  <ul className="mt-5 space-y-2">
                    {(p.features || []).map((f, fi) => <li key={fi} className="text-sm flex items-start gap-2"><Check className={`w-4 h-4 ${featured ? "text-leaf" : "text-brand-green"} mt-0.5 shrink-0`}/>{f}</li>)}
                  </ul>
                  <button onClick={goSignIn} data-testid={`pricing-cta-${i}`} className={`mt-7 w-full rounded-full py-3.5 font-bold transition-all ${featured ? "bg-brand text-white hover:bg-brand-hover" : "bg-ink text-white hover:bg-brand"}`}>{cta}</button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* GLOBAL READY — investor story */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-24 grid lg:grid-cols-[1fr_1fr] gap-12 items-center">
        <div>
          <div className="text-xs font-bold tracking-[0.25em] uppercase text-brand">{t("global.eyebrow")}</div>
          <h2 className="font-heading text-4xl md:text-6xl font-extrabold mt-3 leading-[1.02] tracking-tight">{t("global.heading")}</h2>
          <p className="text-ink-soft mt-5 text-lg max-w-lg">{t("global.sub")}</p>
          <div className="grid grid-cols-2 gap-3 mt-8">
            {[
              { ic: Languages, l: "Multi-language", v: "EN · हिन्दी (more soon)" },
              { ic: Globe2, l: "Multi-currency", v: "INR · USD · GBP · EUR" },
              { ic: ShieldCheck, l: "Regional KYC", v: "ABC Rules · GDPR ready" },
              { ic: Clock, l: "Live ops", v: "24/7 AI + slot booking" },
            ].map((it, i) => (
              <div key={i} className="bg-surface border border-line rounded-2xl p-4">
                <it.ic className="w-4 h-4 text-brand"/>
                <div className="font-bold text-sm mt-1.5">{it.l}</div>
                <div className="text-[11px] text-ink-soft">{it.v}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-leaf/30 border border-leaf rounded-[2rem] p-8">
          <div className="text-xs font-bold tracking-wider uppercase text-brand">By the numbers</div>
          <div className="grid grid-cols-2 gap-5 mt-5">
            <NumBig n="0%" l="fee on verified NGO donations"/>
            <NumBig n="EN · हिं" l="multi-lingual day one"/>
            <NumBig n="60s" l="vet booking flow target"/>
            <NumBig n="₹0" l="free 7-day Premium trial"/>
          </div>
          <p className="text-sm text-ink-soft mt-6 leading-relaxed">Built for India's 100M+ pet households and growing globally — unit-economics-first design that scales without burning capital.</p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden bg-ink text-bg">
        <div className="absolute -top-20 -left-20 w-96 h-96 bg-brand/30 rounded-full blur-3xl"/>
        <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-brand-blue/30 rounded-full blur-3xl"/>
        <div className="relative max-w-4xl mx-auto px-5 md:px-10 py-28 text-center">
          <h2 className="font-heading text-5xl md:text-7xl font-extrabold leading-[1.02] tracking-tight">Join the <span className="italic font-medium text-brand">PetBond</span> family today.</h2>
          <p className="opacity-80 mt-6 text-lg max-w-lg mx-auto">Your pet's whole world — vets, food, AI, friends, IDs, NGOs — finally in one place.</p>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <button data-testid="final-cta-primary" onClick={goSignIn} className="bg-brand text-white rounded-full px-8 py-4 font-bold inline-flex items-center gap-2 hover:bg-brand-hover shadow-[0_8px_30px_rgb(255,107,53,0.45)]">Get started — it's free <ArrowRight className="w-4 h-4"/></button>
            <button onClick={goPartner} className="border border-bg/30 text-bg/90 rounded-full px-7 py-4 font-bold hover:bg-bg/10">List your business</button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-bg border-t border-line">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-14 grid md:grid-cols-4 gap-10">
          <div className="col-span-2">
            <img src="/brand/petbond-logo.png" alt="PetBond" className="h-16 w-auto"/>
            <p className="text-sm text-ink-soft mt-3 max-w-xs">{t("footer.tagline")} — India's first complete pet platform. Built with ♥ for every paw.</p>
            <div className="mt-4 space-y-1.5 text-sm">
              <div><a href="https://www.petbond.in" className="font-bold hover:text-brand" data-testid="footer-domain">www.petbond.in</a></div>
              <div><a href="mailto:hello@petbond.in" className="text-ink-soft hover:text-brand">hello@petbond.in</a></div>
              <div><a href="tel:+919819793270" className="text-ink-soft hover:text-brand" data-testid="footer-phone">+91 98197 93270</a></div>
            </div>
            <div className="mt-4"><LanguageSwitcher/></div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-ink-soft">Product</div>
            <ul className="mt-3 space-y-2 text-sm font-bold">
              <li><a href="#features" className="hover:text-brand">Features</a></li>
              <li><a href="#rescue" className="hover:text-brand">Rescue-to-Home</a></li>
              <li><a href="#vet-care" className="hover:text-brand">Vet Care</a></li>
              <li><a href="#pricing" className="hover:text-brand">Pricing</a></li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-ink-soft">Get in touch</div>
            <ul className="mt-3 space-y-2 text-sm font-bold">
              <li><button onClick={goPartner} className="hover:text-brand text-left">List your business</button></li>
              <li><button onClick={replayTour} className="hover:text-brand text-left">Take the tour</button></li>
              <li><a href="mailto:hello@petbond.in" className="hover:text-brand">Email support</a></li>
              <li><a href="https://wa.me/919819793270" target="_blank" rel="noreferrer" className="hover:text-brand inline-flex items-center gap-1" data-testid="footer-whatsapp">WhatsApp us</a></li>
              <li><Link to="/terms" className="hover:text-brand" data-testid="footer-terms">Terms of Service</Link></li>
              <li><Link to="/privacy" className="hover:text-brand" data-testid="footer-privacy">Privacy Policy</Link></li>
              <li><Link to="/refund" className="hover:text-brand" data-testid="footer-refund">Refund Policy</Link></li>
              <li><Link to="/cancellation" className="hover:text-brand" data-testid="footer-cancellation">Cancellation Policy</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-line">
          <div className="max-w-7xl mx-auto px-5 md:px-10 py-5 text-xs text-ink-soft flex flex-wrap justify-between gap-3">
            <span>© {new Date().getFullYear()} PetBond · www.petbond.in</span>
            <span className="inline-flex items-center gap-1.5">🇮🇳 {t("footer.made")}</span>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
      `}</style>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">{n}</div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-ink-soft mt-1">{l}</div>
    </div>
  );
}

function NumBig({ n, l }) {
  return (
    <div>
      <div className="font-heading text-4xl font-extrabold text-ink">{n}</div>
      <div className="text-[11px] uppercase tracking-wider font-bold text-ink-soft mt-1">{l}</div>
    </div>
  );
}

const PILLARS = [
  {
    title: "AI Health Companion",
    summary: "Dr. PetBond AI checks symptoms in seconds and writes a digital prescription you can save forever.",
    icon: Sparkles,
    color: "bg-brand text-white",
    glow: "bg-brand",
    badge: "24/7",
    features: [
      { label: "Symptom check", icon: Activity,    tip: "AI triage with built-in red-flag guardrails — emergencies are flagged with vet contact." },
      { label: "Digital Rx",    icon: Pill,        tip: "Every vet visit produces a portable digital prescription stored in the Pet Passport." },
      { label: "Pet passport",  icon: FileText,    tip: "Unified medical timeline across vets, vaccinations, allergies and surgeries." },
      { label: "Auto reminders",icon: Syringe,     tip: "Vaccination & deworming reminders trigger before due date — no missed shots." },
    ],
  },
  {
    title: "Verified Care Network",
    summary: "KYC-verified vets, groomers, walkers, hostels & mobile vet vans — all bookable in 60 seconds.",
    icon: Stethoscope,
    color: "bg-brand-blue text-white",
    glow: "bg-brand-blue",
    features: [
      { label: "Real-time slots", icon: Calendar, tip: "Vet calendars are live — pick a free slot and confirm, no calls back-and-forth." },
      { label: "Groomers & walkers", icon: Scissors, tip: "Bath/haircut/nail-trim + daily/group walks. Verified pros with reviews." },
      { label: "Mobile vet vans", icon: Truck, tip: "GPS-pinned vaccination drives. Nearby parents auto-notified when one is scheduled." },
      { label: "Hostels", icon: Building2, tip: "Day-by-day boarding with live capacity. Cage-free filter + CCTV options." },
    ],
  },
  {
    title: "Rescue-to-Home Pipeline",
    summary: "The world's first NGO ↔ Vet ↔ Foster ↔ Sponsor pipeline. Every step audit-trailed.",
    icon: HandHeart,
    color: "bg-brand-green text-white",
    glow: "bg-brand-green",
    badge: "Flagship",
    features: [
      { label: "Intake & rescues", icon: HandHeart, tip: "NGOs log strays with GPS, photos and case notes — assigned to vets and fosters in-app." },
      { label: "Adoption listings", icon: Heart, tip: "ABC-compliant rescues turn into adoption posts. Apply in one tap, privacy-first contact reveal." },
      { label: "Foster pipeline", icon: Heart, tip: "Foster applications include ID + home photos. Auto-routed to nearby NGOs." },
      { label: "Sponsor a pet", icon: Gift, tip: "₹500/month keeps the lights on. 0% platform fee on all verified-NGO donations." },
    ],
  },
  {
    title: "Court-Ready Compliance",
    summary: "ABC Rules 2023 register, GST-compliant invoices, 80G donation receipts — print on demand.",
    icon: ShieldCheck,
    color: "bg-sun text-ink",
    glow: "bg-sun",
    badge: "ABC 2023",
    features: [
      { label: "Animal register", icon: FileText, tip: "Court-mandated register of every captured/sterilised/released animal — generates PDF in your NGO letterhead." },
      { label: "Pet licence concierge", icon: ShieldCheck, tip: "Pet parents struggle with civic registration. PetBond's concierge files with the Noida Authority, MCD, MCG, GMC, BMC, BBMP, PMC, GCC, GHMC and more — flat ₹999 + actual govt fee — certificate delivered to your inbox." },
      { label: "80G receipts", icon: Gift, tip: "Auto-generated tax-deductible receipts for every donation — instant to donor's inbox." },
      { label: "GDPR-ready", icon: ShieldCheck, tip: "Privacy-first server-mediated contact, data export & delete on request." },
    ],
  },
  {
    title: "Pet Identity & Safety",
    summary: "Printable QR tag, public scan page, lost-and-found radar — your pet is never truly lost.",
    icon: QrCode,
    color: "bg-ink text-white",
    glow: "bg-ink",
    features: [
      { label: "QR Pet ID", icon: QrCode, tip: "Printable QR poster — finder scans → notifies owner via privacy-preserving relay (no number leaks)." },
      { label: "Lost & Found", icon: MapPin, tip: "City-wide radar of lost & found pets with last-seen GPS pins on the map." },
      { label: "Live GPS track", icon: MapPin, tip: "Walk recorder + future collar webhook for real-time location during walks." },
      { label: "Meetups", icon: Users, tip: "AI-suggested midway venues for pet playdates with vetted families nearby." },
    ],
  },
  {
    title: "Marketplace & Growth",
    summary: "Hyper-local pet store · one cart per store · weekly partner payouts. Built for unit economics.",
    icon: ShoppingBag,
    color: "bg-brand text-white",
    glow: "bg-brand",
    features: [
      { label: "Nearby-first", icon: MapPin, tip: "Near me / All India toggle — products, vets, hostels & NGOs sorted by distance from you, with a 25 km default radius." },
      { label: "One-store cart", icon: ShoppingBag, tip: "Like Swiggy: one cart per store = one delivery, one bill, fast fulfillment. Switch stores anytime in a single tap." },
      { label: "Premium 7-day", icon: Crown, tip: "7-day free trial unlocks unlimited AI, priority vet booking & premium foster matches." },
      { label: "Partner payouts", icon: Wallet, tip: "Auto-ledger of every booking with weekly payouts to UPI/bank via Razorpay Route." },
    ],
  },
];

// Fallback used only if /api/landing-pricing is unreachable or empty. Admin
// edits in /app/admin/plans take precedence at runtime.
const PRICING_FALLBACK = [
  { tag: "Free", name: "Forever Free", price_display: "₹0", unit_display: "/forever", tagline: "Everything you need to start.", cta_label: "Sign up free", features: ["AI symptom check (3/day)", "Pet profile + QR ID", "Adopt / Foster / Lost & Found", "Community meetups"] },
  { tag: "Premium", name: "Premium Monthly", price_display: "₹299", unit_display: "/month", tagline: "Unlimited AI + priority everything.", featured: true, cta_label: "Start 7-day free trial", features: ["AI 24/7 unlimited", "Priority vet booking", "Premium foster matches", "Tax-receipt for donations", "Unlimited Rx history"] },
  { tag: "Yearly", name: "Premium Yearly", price_display: "₹2,499", unit_display: "/year", tagline: "Save ₹1,089 — best value.", cta_label: "Choose Yearly", features: ["Everything in Monthly", "2 free vet consults/year", "Annual pet health report", "Priority phone support"] },
];
